import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Fish, Anchor, MapPin, Thermometer, ShieldCheck, Scale } from 'lucide-react';

// ---------------------------------------------------------------------------
// Data: 22 species with seasonal availability
// ---------------------------------------------------------------------------

interface MariscoSpecies {
  name: string;
  emoji: string;
  peak: number[];     // month numbers (1-12)
  available: number[]; // month numbers (1-12)
  zone: string;
}

const MARISCOS: MariscoSpecies[] = [
  { name: 'Gamba roja', emoji: '\uD83E\uDD90', peak: [5, 6, 7, 8], available: [4, 5, 6, 7, 8, 9], zone: 'Mediterráneo' },
  { name: 'Langostino', emoji: '\uD83E\uDD90', peak: [5, 6, 7, 8], available: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12], zone: 'Huelva / Mediterráneo' },
  { name: 'Cigala', emoji: '\uD83E\uDD9E', peak: [3, 4, 5, 6], available: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], zone: 'Cantábrico / Galicia' },
  { name: 'Bogavante', emoji: '\uD83E\uDD9E', peak: [11, 12, 1, 2], available: [10, 11, 12, 1, 2, 3], zone: 'Cantábrico / Galicia' },
  { name: 'Buey de mar', emoji: '\uD83E\uDD80', peak: [10, 11, 12], available: [9, 10, 11, 12, 1, 2], zone: 'Cantábrico / Galicia' },
  { name: 'Centollo', emoji: '\uD83E\uDD80', peak: [11, 12, 1], available: [10, 11, 12, 1, 2, 3], zone: 'Galicia' },
  { name: 'Nécora', emoji: '\uD83E\uDD80', peak: [11, 12, 1], available: [10, 11, 12, 1, 2, 3], zone: 'Galicia' },
  { name: 'Percebe', emoji: '\uD83E\uDDAA', peak: [10, 11, 12, 1], available: [9, 10, 11, 12, 1, 2, 3], zone: 'Costa da Morte' },
  { name: 'Mejillón', emoji: '\uD83E\uDDAA', peak: [5, 6, 7, 8], available: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], zone: 'Galicia / Delta del Ebro' },
  { name: 'Almeja', emoji: '\uD83E\uDDAA', peak: [11, 12, 1, 2], available: [9, 10, 11, 12, 1, 2, 3, 4], zone: 'Galicia / Cádiz' },
  { name: 'Navaja', emoji: '\uD83E\uDDAA', peak: [3, 4, 5], available: [2, 3, 4, 5, 6, 7], zone: 'Galicia / Cantábrico' },
  { name: 'Berberecho', emoji: '\uD83E\uDDAA', peak: [11, 12, 1, 2], available: [9, 10, 11, 12, 1, 2, 3], zone: 'Galicia / Huelva' },
  { name: 'Vieira', emoji: '\uD83E\uDDAA', peak: [11, 12, 1], available: [10, 11, 12, 1, 2, 3], zone: 'Galicia' },
  { name: 'Ostra', emoji: '\uD83E\uDDAA', peak: [10, 11, 12, 1, 2], available: [9, 10, 11, 12, 1, 2, 3], zone: 'Galicia / Delta del Ebro' },
  { name: 'Carabinero', emoji: '\uD83E\uDD90', peak: [10, 11, 12], available: [9, 10, 11, 12, 1, 2], zone: 'Huelva / Cádiz' },
  { name: 'Langosta', emoji: '\uD83E\uDD9E', peak: [6, 7, 8], available: [5, 6, 7, 8, 9], zone: 'Mediterráneo / Baleares' },
  { name: 'Cangrejo de río', emoji: '\uD83E\uDD80', peak: [6, 7, 8], available: [5, 6, 7, 8, 9], zone: 'Ríos interiores' },
  { name: 'Zamburiña', emoji: '\uD83E\uDDAA', peak: [11, 12, 1], available: [10, 11, 12, 1, 2, 3], zone: 'Galicia' },
  { name: 'Coquina', emoji: '\uD83E\uDDAA', peak: [5, 6, 7], available: [4, 5, 6, 7, 8], zone: 'Huelva / Cádiz' },
  { name: 'Erizo de mar', emoji: '\uD83E\uDDAA', peak: [12, 1, 2, 3], available: [11, 12, 1, 2, 3, 4], zone: 'Cádiz / Galicia' },
  { name: 'Gamba blanca', emoji: '\uD83E\uDD90', peak: [3, 4, 5, 6], available: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], zone: 'Huelva' },
  { name: 'Pulpo', emoji: '\uD83D\uDC19', peak: [7, 8, 9], available: [6, 7, 8, 9, 10, 11], zone: 'Galicia / Canarias' },
];

const MONTH_NAMES = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

const MONTH_FULL = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// ---------------------------------------------------------------------------
// Seasonal groupings
// ---------------------------------------------------------------------------

const SEASONS = [
  {
    name: 'Primavera',
    icon: '\uD83C\uDF38',
    months: 'Marzo - Mayo',
    color: 'bg-emerald-50 border-emerald-200',
    textColor: 'text-emerald-700',
    species: ['Cigala', 'Langostino', 'Navaja', 'Berberecho', 'Gamba blanca', 'Coquina'],
    note: 'Las aguas empiezan a templarse y los crustáceos de primavera ganan sabor. La navaja y la coquina están en su mejor momento.',
  },
  {
    name: 'Verano',
    icon: '\u2600\uFE0F',
    months: 'Junio - Agosto',
    color: 'bg-amber-50 border-amber-200',
    textColor: 'text-amber-700',
    species: ['Gamba roja', 'Mejillón', 'Coquina', 'Langosta', 'Cangrejo de río', 'Pulpo'],
    note: 'La gamba roja del Mediterráneo está en su apogeo. El mejillón y el pulpo ofrecen opciones con excelente margen.',
  },
  {
    name: 'Otoño',
    icon: '\uD83C\uDF42',
    months: 'Septiembre - Noviembre',
    color: 'bg-orange-50 border-orange-200',
    textColor: 'text-orange-700',
    species: ['Centollo', 'Nécora', 'Buey de mar', 'Percebe', 'Carabinero', 'Ostra'],
    note: 'Arranca la gran temporada del marisco gallego. Centollo, nécora y percebes son los reyes indiscutibles.',
  },
  {
    name: 'Invierno',
    icon: '\u2744\uFE0F',
    months: 'Diciembre - Febrero',
    color: 'bg-sky-50 border-sky-200',
    textColor: 'text-sky-700',
    species: ['Bogavante', 'Ostra', 'Vieira', 'Percebe', 'Almeja', 'Erizo de mar'],
    note: 'El invierno es la temporada alta del marisco premium. Bogavante, vieiras y ostras alcanzan su máxima calidad y sabor.',
  },
];

// ---------------------------------------------------------------------------
// Price comparison: in season vs out of season
// ---------------------------------------------------------------------------

const PRICE_COMPARISON = [
  { species: 'Centollo', inSeason: '30-40', outSeason: '55-70', saving: '40%' },
  { species: 'Percebes', inSeason: '35-50', outSeason: '70-100', saving: '45%' },
  { species: 'Gamba roja', inSeason: '40-55', outSeason: '65-80', saving: '35%' },
  { species: 'Nécora', inSeason: '20-30', outSeason: '40-55', saving: '45%' },
  { species: 'Bogavante', inSeason: '35-45', outSeason: '60-80', saving: '40%' },
  { species: 'Vieira', inSeason: '18-25', outSeason: '35-50', saving: '45%' },
  { species: 'Langostino', inSeason: '12-18', outSeason: '22-30', saving: '40%' },
  { species: 'Carabinero', inSeason: '50-70', outSeason: '90-120', saving: '40%' },
  { species: 'Erizo de mar', inSeason: '15-25', outSeason: '35-50', saving: '50%' },
  { species: 'Ostra', inSeason: '1.5-2.5/ud', outSeason: '3-4.5/ud', saving: '45%' },
];

// ---------------------------------------------------------------------------
// Mermas del marisco
// ---------------------------------------------------------------------------

const MERMAS = [
  { species: 'Langostino', merma: '55%', aprovechamiento: '45%', note: 'Cabeza y cáscara. Caldo de marisco con las cabezas.' },
  { species: 'Cigala', merma: '60%', aprovechamiento: '40%', note: 'Alto desperdicio. Bizcochetas y caldos con los restos.' },
  { species: 'Mejillón', merma: '65%', aprovechamiento: '35%', note: 'Concha pesada. Comprar por kg de carne limpia.' },
  { species: 'Bogavante', merma: '50%', aprovechamiento: '50%', note: 'Caparazón grueso. Carcasas para bisqué.' },
  { species: 'Percebe', merma: '40%', aprovechamiento: '60%', note: 'Pedúnculo comestible. Rendimiento variable por calibre.' },
  { species: 'Centollo', merma: '55%', aprovechamiento: '45%', note: 'Caparazón y patas finas. Coral muy valorado.' },
  { species: 'Gamba roja', merma: '45%', aprovechamiento: '55%', note: 'Cabezas para chupar o para bisqué. Producto premium.' },
  { species: 'Almeja', merma: '60%', aprovechamiento: '40%', note: 'Concha y arena. Purgar mínimo 2h en agua con sal.' },
  { species: 'Vieira', merma: '50%', aprovechamiento: '50%', note: 'Concha y vísceras. Nuez y coral son lo aprovechable.' },
  { species: 'Buey de mar', merma: '55%', aprovechamiento: '45%', note: 'Pinzas con carne excelente. Cuerpo con coral.' },
];

// ---------------------------------------------------------------------------
// Zonas marisqueras
// ---------------------------------------------------------------------------

const ZONAS = [
  {
    name: 'Galicia',
    description: 'La capital mundial del marisco. Sus rías proporcionan centollo, nécora, percebe, vieira, zamburiña, mejillón y almeja de calidad insuperable.',
    species: 'Centollo, nécora, percebe, vieira, zamburiña, mejillón, almeja, berberecho, navaja, pulpo',
    highlight: 'Las Rías Baixas (Pontevedra, Arousa, Vigo) y Costa da Morte (percebes)',
  },
  {
    name: 'Huelva',
    description: 'Famosa por su gamba blanca de Huelva y las coquinas de la playa de Isla Cristina. Producto atlántico con sabor propio.',
    species: 'Gamba blanca, langostino, coquina, berberecho, cigala',
    highlight: 'Puerto de Huelva e Isla Cristina',
  },
  {
    name: 'Cádiz',
    description: 'De las aguas del estrecho llegan carabineros, langostinos y los mejores erizos de mar de la península. Sanlúcar de Barrameda es referencia.',
    species: 'Carabinero, langostino, erizo de mar, ortiguilla, bocas de la Isla',
    highlight: 'Sanlúcar de Barrameda, El Puerto de Santa María',
  },
  {
    name: 'Delta del Ebro',
    description: 'Zona de aguas salobres donde se cultivan mejillones y ostras de gran calidad. Producto de proximidad para la hostelería catalana.',
    species: 'Mejillón, ostra, almeja, langostino',
    highlight: 'Badia dels Alfacs, Sant Carles de la Ràpita',
  },
  {
    name: 'Cantábrico',
    description: 'Aguas frías y bravas que producen cigala, bogavante y centollo de talla grande. Laredo, Santoña y San Vicente de la Barquera son puertos de referencia.',
    species: 'Cigala, bogavante, centollo, nécora, percebe, buey de mar',
    highlight: 'Santoña, Laredo, Castro Urdiales',
  },
];

// ---------------------------------------------------------------------------
// FAQs
// ---------------------------------------------------------------------------

const faqs = [
  {
    q: '¿Cuándo es la mejor temporada de marisco en España?',
    a: 'La mejor temporada general de marisco en España es de octubre a marzo, cuando el marisco gallego (centollo, nécora, percebe, vieira) está en su punto óptimo. Sin embargo, cada especie tiene su propia temporada: la gamba roja es mejor en verano (mayo-agosto) y el mejillón está disponible todo el año con pico en verano.',
  },
  {
    q: '¿Qué marisco está de temporada ahora?',
    a: 'Depende del mes. En invierno (diciembre-febrero): centollo, bogavante, ostras, vieiras, percebes. En primavera (marzo-mayo): cigalas, navajas, gamba blanca. En verano (junio-agosto): gamba roja, mejillón, langosta, coquina. En otoño (septiembre-noviembre): centollo, nécora, percebes, carabinero.',
  },
  {
    q: '¿Cuánto más barato es el marisco en temporada?',
    a: 'El marisco de temporada puede costar entre un 35% y un 50% menos que fuera de temporada. Por ejemplo, el centollo pasa de 55-70 euros/kg fuera de temporada a 30-40 euros/kg en temporada. Los percebes bajan de 70-100 euros a 35-50 euros/kg. Estos ahorros son enormes para la hostelería.',
  },
  {
    q: '¿Cómo afecta la temporada del marisco al food cost de un restaurante?',
    a: 'La temporada del marisco puede reducir el food cost de los platos de marisco entre un 20% y un 40%. Un restaurante que adapta su carta a la estacionalidad del marisco mejora márgenes y ofrece mejor calidad. Con PLEKO puedes calcular el escandallo de cada plato de marisco y ver cómo cambia el food cost mes a mes.',
  },
  {
    q: '¿Cuál es la merma media del marisco?',
    a: 'La merma media del marisco oscila entre el 40% y el 65%, dependiendo de la especie. Los crustáceos con caparazón (cigala 60%, centollo 55%) tienen más merma que los que se sirven enteros (percebe 40%). Los bivalvos (mejillón 65%, almeja 60%) tienen mucha concha. Es fundamental calcular la merma para fijar precios correctamente.',
  },
  {
    q: '¿Dónde se pesca el mejor marisco en España?',
    a: 'Galicia es la referencia indiscutible para marisco en España, especialmente las Rías Baixas y la Costa da Morte. Huelva destaca por la gamba blanca y las coquinas. Cádiz aporta carabineros y erizos. El Cantábrico produce cigalas y bogavantes de gran tamaño. El Delta del Ebro ofrece mejillón y ostra de calidad.',
  },
  {
    q: '¿Cómo conservar el marisco fresco en un restaurante?',
    a: 'El marisco vivo debe mantenerse entre 2-4 grados C, cubierto con paños húmedos (nunca en agua dulce). Los crustáceos vivos se conservan 24-48h. Los bivalvos cerrados aguantan 3-5 días en frío. El marisco cocido debe consumirse en 24h. Los cefalópodos frescos, 2-3 días en nevera. Siempre comprobar frescura antes del servicio.',
  },
  {
    q: '¿Es mejor comprar marisco fresco o congelado para hostelería?',
    a: 'Para platos principales y presentaciones premium, el marisco fresco es insustituible. Para caldos, arroces, bisqués y rellenos, el congelado de calidad (ultracongelado en origen) ofrece buena relación calidad-precio. La clave es calcular el escandallo de ambas opciones y decidir según el plato y el precio de venta.',
  },
];

// ---------------------------------------------------------------------------
// JSON-LD structured data
// ---------------------------------------------------------------------------

const jsonLdFaq = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const jsonLdBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Calendario temporada', item: 'https://pleko.app/calendario-temporada' },
    { '@type': 'ListItem', position: 3, name: 'Marisco de temporada', item: 'https://pleko.app/marisco-de-temporada' },
  ],
};

const jsonLdArticle = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Marisco de Temporada en España: Calendario Completo y Guía de Precios',
  description: 'Calendario completo de marisco de temporada en España con 22 especies, precios por temporada, mermas, zonas marisqueras y consejos para hostelería.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: { '@type': 'Organization', name: 'PLEKO', url: 'https://pleko.app' },
  datePublished: '2026-06-18',
  dateModified: '2026-06-18',
  mainEntityOfPage: 'https://pleko.app/marisco-de-temporada',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MariscoTemporadaPage() {
  const currentMonth = new Date().getMonth() + 1;

  return (
    <>
      <Helmet>
        <title>Marisco de Temporada en España 2026 — Calendario Completo | PLEKO</title>
        <meta
          name="description"
          content="Calendario completo de marisco de temporada en España: 22 especies, meses de disponibilidad, precios, mermas y zonas marisqueras. Guía práctica para hostelería."
        />
        <meta
          name="keywords"
          content="marisco de temporada, marisco temporada españa, calendario marisco temporada, qué marisco está de temporada, marisco fresco españa, temporada marisco gallego, precios marisco temporada, centollo temporada, gamba roja temporada, percebe temporada"
        />
        <link rel="canonical" href="https://pleko.app/marisco-de-temporada" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/marisco-de-temporada" />
        <meta property="og:title" content="Marisco de Temporada en España 2026 — Calendario Completo | PLEKO" />
        <meta
          property="og:description"
          content="Calendario completo de marisco de temporada en España: 22 especies, precios, mermas y zonas marisqueras. Guía para restaurantes."
        />
        <meta property="og:image" content="https://pleko.app/og-image.png" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Marisco de Temporada en España 2026 — Calendario Completo | PLEKO" />
        <meta name="twitter:description" content="Calendario de marisco de temporada en España: 22 especies, precios por temporada, mermas y zonas marisqueras." />
        <meta name="twitter:image" content="https://pleko.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify([jsonLdFaq, jsonLdBreadcrumb, jsonLdArticle])}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-teal-50 to-white py-16 lg:py-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-6">
                <Fish className="w-4 h-4" />
                Calendario de marisco de temporada en España — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
                Marisco de <span className="text-teal-600">Temporada</span> en España: Calendario Completo y Guía de Precios
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
                22 especies de marisco con sus meses de disponibilidad, precios en temporada vs fuera de temporada, mermas,
                zonas marisqueras y consejos para integrar el marisco de temporada en tu carta.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <a href="#calendario" className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors">
                  Ver calendario <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#precios" className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-teal-200 text-teal-700 rounded-lg font-semibold hover:bg-teal-50 transition-colors">
                  Ver precios <Scale className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Section 1: Por qué el marisco de temporada marca la diferencia */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Por qué el marisco de temporada marca la diferencia
            </h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              Comprar marisco en su temporada óptima no es solo una cuestión de precio — afecta directamente a la calidad de tu cocina y la rentabilidad de tu negocio.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: 'Frescura máxima',
                  desc: 'El marisco capturado en su temporada natural tiene las carnes más firmes, llenas y sabrosas. Un centollo de noviembre no tiene nada que ver con uno de junio.',
                  icon: Thermometer,
                  color: 'bg-teal-100 text-teal-600',
                },
                {
                  title: 'Precio 35-50% menor',
                  desc: 'La abundancia en temporada reduce los precios de lonja. Un restaurante que compra en temporada puede ahorrar miles de euros al año en materia prima.',
                  icon: Scale,
                  color: 'bg-emerald-100 text-emerald-600',
                },
                {
                  title: 'Sabor superior',
                  desc: 'El ciclo biológico del marisco determina cuándo tiene más contenido graso y carne. El bogavante de invierno o la gamba roja de verano son productos incomparables.',
                  icon: Fish,
                  color: 'bg-cyan-100 text-cyan-600',
                },
                {
                  title: 'Sostenibilidad',
                  desc: 'Respetar las vedas y las temporadas contribuye a la conservación de las especies y las artes de pesca artesanal. Los comensales valoran cada vez más esta responsabilidad.',
                  icon: Anchor,
                  color: 'bg-blue-100 text-blue-600',
                },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className={`w-14 h-14 ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <item.icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 2: GRAN TABLA - Calendario anual de marisco */}
        <section id="calendario" className="bg-slate-50 py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Calendario anual de marisco de temporada
            </h2>
            <p className="text-slate-600 text-center mb-3 max-w-2xl mx-auto">
              22 especies de marisco con sus meses de disponibilidad y pico de temporada.
            </p>
            <div className="flex items-center justify-center gap-6 mb-8 text-sm">
              <span className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 rounded bg-teal-500" /> Pico de temporada
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 rounded bg-teal-200" /> Disponible
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 rounded bg-slate-100" /> Fuera de temporada
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[800px] w-full text-sm bg-white rounded-xl border border-slate-200 overflow-hidden">
                <thead>
                  <tr className="bg-teal-600 text-white">
                    <th className="text-left py-3 px-4 font-semibold w-44">Especie</th>
                    {MONTH_NAMES.map((name, i) => (
                      <th
                        key={i}
                        className={`text-center py-3 px-1 font-medium text-xs ${
                          i + 1 === currentMonth ? 'bg-teal-700' : ''
                        }`}
                      >
                        {name}
                      </th>
                    ))}
                    <th className="text-left py-3 px-3 font-semibold text-xs">Zona</th>
                  </tr>
                </thead>
                <tbody>
                  {MARISCOS.map((m, idx) => (
                    <tr key={m.name} className={`border-t border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className="py-2 px-4 font-medium text-slate-800">
                        <span className="mr-1.5">{m.emoji}</span>
                        {m.name}
                      </td>
                      {Array.from({ length: 12 }, (_, i) => {
                        const month = i + 1;
                        const isPeak = m.peak.includes(month);
                        const isAvail = m.available.includes(month);
                        const isCurrent = month === currentMonth;
                        return (
                          <td
                            key={month}
                            className={`text-center py-2 px-1 ${isCurrent ? 'ring-1 ring-inset ring-teal-400' : ''}`}
                          >
                            {isPeak ? (
                              <span className="inline-block w-5 h-5 rounded bg-teal-500" title={`${m.name} - Pico en ${MONTH_FULL[i]}`} />
                            ) : isAvail ? (
                              <span className="inline-block w-5 h-5 rounded bg-teal-200" title={`${m.name} - Disponible en ${MONTH_FULL[i]}`} />
                            ) : (
                              <span className="inline-block w-5 h-5 rounded bg-slate-100" />
                            )}
                          </td>
                        );
                      })}
                      <td className="py-2 px-3 text-xs text-slate-500">{m.zone}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-4 text-center">
              * Las temporadas pueden variar ligeramente según condiciones climáticas y vedas locales. Columna destacada: mes actual ({MONTH_FULL[currentMonth - 1]}).
            </p>
          </div>
        </section>

        {/* Section 3: Marisco por estación */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Marisco por estación del año
            </h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              Cada estación tiene sus especies estrella. Planifica tu carta con antelación para ofrecer siempre el marisco en su mejor momento.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {SEASONS.map((season) => (
                <div key={season.name} className={`rounded-xl border p-6 ${season.color}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">{season.icon}</span>
                    <div>
                      <h3 className={`text-xl font-bold ${season.textColor}`}>{season.name}</h3>
                      <p className="text-sm text-slate-500">{season.months}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {season.species.map((sp) => (
                      <span key={sp} className="px-3 py-1 bg-white/70 rounded-full text-sm font-medium text-slate-700">
                        {sp}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{season.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 4: Tabla de precios temporada vs fuera */}
        <section id="precios" className="bg-teal-50 py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Precios: temporada vs fuera de temporada
            </h2>
            <p className="text-slate-600 text-center mb-10 max-w-2xl mx-auto">
              La diferencia de precios entre comprar marisco en temporada y fuera de ella puede superar el 40%.
              Para la hostelería, estos ahorros son la diferencia entre un plato rentable y uno que da pérdidas.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm bg-white rounded-xl border border-teal-200 overflow-hidden">
                <thead>
                  <tr className="bg-teal-600 text-white">
                    <th className="text-left py-3 px-4 font-semibold">Especie</th>
                    <th className="text-center py-3 px-4 font-semibold">En temporada (euros/kg)</th>
                    <th className="text-center py-3 px-4 font-semibold">Fuera de temporada (euros/kg)</th>
                    <th className="text-center py-3 px-4 font-semibold">Ahorro</th>
                  </tr>
                </thead>
                <tbody>
                  {PRICE_COMPARISON.map((row, i) => (
                    <tr key={row.species} className={`border-t border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-teal-50/30'}`}>
                      <td className="py-3 px-4 font-medium text-slate-800">{row.species}</td>
                      <td className="py-3 px-4 text-center text-teal-700 font-semibold">{row.inSeason} euros</td>
                      <td className="py-3 px-4 text-center text-red-600">{row.outSeason} euros</td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
                          -{row.saving}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-4 text-center">
              * Precios orientativos de mercados mayoristas (Mercamadrid, Mercabarna). Pueden variar por calibre, origen y demanda.
            </p>
          </div>
        </section>

        {/* Section 5: Mejores zonas de marisco en España */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Las mejores zonas de marisco en España
            </h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              España tiene algunas de las mejores zonas marisqueras de Europa. Conocer el origen te permite seleccionar proveedores con producto superior.
            </p>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {ZONAS.map((zona) => (
                <div key={zona.name} className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-5 h-5 text-teal-600" />
                    <h3 className="text-lg font-bold text-slate-900">{zona.name}</h3>
                  </div>
                  <p className="text-slate-600 text-sm mb-3 leading-relaxed">{zona.description}</p>
                  <p className="text-xs text-teal-700 font-medium mb-2">Especies: {zona.species}</p>
                  <p className="text-xs text-slate-400">Puertos: {zona.highlight}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 6: Mermas del marisco */}
        <section className="bg-slate-50 py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Mermas del marisco: tabla de aprovechamiento
            </h2>
            <p className="text-slate-600 text-center mb-10 max-w-2xl mx-auto">
              Conocer la merma exacta de cada marisco es imprescindible para calcular el coste real por ración y fijar precios correctos.
              Sin esta información, es imposible saber si un plato de marisco da beneficio o pérdida.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm bg-white rounded-xl border border-slate-200 overflow-hidden">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="text-left py-3 px-4 font-semibold">Especie</th>
                    <th className="text-center py-3 px-4 font-semibold">Merma</th>
                    <th className="text-center py-3 px-4 font-semibold">Aprovechamiento</th>
                    <th className="text-left py-3 px-4 font-semibold">Consejo</th>
                  </tr>
                </thead>
                <tbody>
                  {MERMAS.map((row, i) => (
                    <tr key={row.species} className={`border-t border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className="py-3 px-4 font-medium text-slate-800">{row.species}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                          {row.merma}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
                          {row.aprovechamiento}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-xs">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 text-center">
              <Link
                to="/tabla-mermas-alimentos"
                className="text-teal-600 hover:text-teal-700 font-medium text-sm hover:underline"
              >
                Ver tabla completa de mermas de todos los alimentos →
              </Link>
            </div>
          </div>
        </section>

        {/* Section 7: Cómo integrar marisco de temporada en tu carta */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-10">
              Cómo integrar marisco de temporada en tu carta
            </h2>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Planifica por trimestres',
                  desc: 'Diseña cuatro cartas al año alineadas con las estaciones del marisco. En otoño-invierno, centollo y percebes. En primavera-verano, gamba roja y coquinas. Tu proveedor te lo agradecerá con mejores precios.',
                },
                {
                  step: '2',
                  title: 'Calcula el escandallo con mermas reales',
                  desc: 'Un bogavante de 50 euros/kg con 50% de merma cuesta realmente 100 euros/kg de carne limpia. Sin calcular la merma, fijarás precios incorrectos. PLEKO lo hace automáticamente para cada ingrediente.',
                },
                {
                  step: '3',
                  title: 'Ofrece "sugerencias de temporada"',
                  desc: 'Una sección de "Recomendaciones de temporada" en tu carta comunica frescura y calidad. Los comensales pagan más por producto que perciben como fresco y de temporada.',
                },
                {
                  step: '4',
                  title: 'Negocia precios con antelación',
                  desc: 'Habla con tu proveedor antes de que empiece la temporada. Cierra precios para volúmenes estables y evita los picos de demanda de Navidad y Semana Santa.',
                },
                {
                  step: '5',
                  title: 'Aprovecha los subproductos',
                  desc: 'Las cabezas de gamba para bisqué, los caparazones para caldos, el coral del centollo para salsas. Un buen control de las mermas del marisco puede generar platos adicionales con coste casi cero.',
                },
              ].map((item) => (
                <div key={item.step} className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-lg flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 8: Conservación y manipulación */}
        <section className="bg-teal-50 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 justify-center mb-10">
              <ShieldCheck className="w-7 h-7 text-teal-600" />
              <h2 className="text-3xl font-bold text-slate-900">
                Conservación y manipulación del marisco fresco
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Crustáceos vivos (centollo, bogavante, cigala)',
                  tips: [
                    'Mantener entre 2-4 grados C con paños húmedos',
                    'Nunca sumergir en agua dulce (los mata)',
                    'Consumir en 24-48h desde recepción',
                    'Comprobar que están vivos antes de cocinar',
                  ],
                },
                {
                  title: 'Bivalvos (almeja, mejillón, ostra)',
                  tips: [
                    'Conservar en nevera entre 2-5 grados C',
                    'Los que estén abiertos y no cierren al golpear, desechar',
                    'Purgar en agua con sal 2-4 horas',
                    'Consumir en 3-5 días máximo',
                  ],
                },
                {
                  title: 'Gambas y langostinos frescos',
                  tips: [
                    'Mantener en hielo picado (0-2 grados C)',
                    'Consumir en 24-48h para máxima frescura',
                    'No romper la cadena de frío en ningún momento',
                    'Cabeza negra = deterioro (no servir)',
                  ],
                },
                {
                  title: 'Marisco cocido',
                  tips: [
                    'Consumir en 24h una vez cocido',
                    'Refrigerar inmediatamente tras la cocción',
                    'No recongelar marisco descongelado',
                    'Etiquetado obligatorio: fecha, hora, caducidad',
                  ],
                },
              ].map((group) => (
                <div key={group.title} className="bg-white rounded-xl border border-teal-200 p-5">
                  <h3 className="font-semibold text-teal-800 mb-3">{group.title}</h3>
                  <ul className="space-y-2">
                    {group.tips.map((tip, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-400 mt-1.5 flex-shrink-0" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-10">
              Preguntas frecuentes sobre marisco de temporada
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <summary className="flex items-center justify-between cursor-pointer px-6 py-4 font-semibold text-slate-900 hover:bg-slate-50 transition-colors">
                    {faq.q}
                    <span className="ml-4 text-slate-400 group-open:rotate-45 transition-transform text-xl">+</span>
                  </summary>
                  <div className="px-6 pb-4 text-slate-600 leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Cross-links */}
        <section className="bg-slate-50 py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
              Guías relacionadas
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {[
                { to: '/calendario-temporada', label: 'Calendario de temporada', emoji: '\uD83C\uDF3F' },
                { to: '/precios-mercado', label: 'Precios de mercado', emoji: '\uD83D\uDCCA' },
                { to: '/tabla-mermas-alimentos', label: 'Tabla de mermas', emoji: '\uD83D\uDCCB' },
                { to: '/escandallo', label: 'Escandallo', emoji: '\uD83E\uDDEE' },
                { to: '/gestion-compras-restaurante', label: 'Gestión de compras', emoji: '\uD83D\uDED2' },
                { to: '/escandallo-catering', label: 'Escandallo catering', emoji: '\uD83C\uDF7D\uFE0F' },
                { to: '/control-costes-restaurante', label: 'Control de costes', emoji: '\uD83D\uDCB0' },
                { to: '/food-cost', label: 'Food cost', emoji: '\uD83D\uDCC9' },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-2 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-teal-300 hover:bg-teal-50 transition-colors text-sm font-medium text-slate-700"
                >
                  <span>{link.emoji}</span>
                  <span>{link.label}</span>
                  <ArrowRight className="w-3.5 h-3.5 ml-auto text-slate-400" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-teal-600 to-cyan-600 py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Controla el coste del marisco en tu carta con PLEKO
            </h2>
            <p className="text-teal-100 text-lg mb-8 max-w-xl mx-auto">
              Calcula el escandallo del marisco con mermas reales, compara precios de temporada
              y optimiza tu food cost automáticamente.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-teal-700 rounded-lg font-bold text-lg hover:bg-teal-50 transition-colors shadow-lg"
            >
              Prueba gratis 14 días <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
